import NavDrawer from './NavDrawer';
import { useState, useEffect, useMemo } from 'react';
import { supabase, fetchAllRows } from './supabaseClient';
import { loadPublishedSets } from './setSource';
import { BASE_PARALLELS, INSERT_PARALLELS, AUTO_PARALLELS, getParallelsForType } from './parallelData';
import { imageUrl, localImageUrl } from './CardImage';
import { lightAccent } from './theme';

const ALL_PARALLELS = [...BASE_PARALLELS, ...INSERT_PARALLELS, ...AUTO_PARALLELS];
const PAR_MAP = {};
ALL_PARALLELS.forEach(p => { if (!PAR_MAP[p.id]) PAR_MAP[p.id] = p; });

function statusLabel(s, price) {
  if (s === 'for_sale') return `$ FOR SALE · $${Number(price||0).toFixed(2)}`;
  if (s === 'trade') return '⇄ FOR TRADE';
  if (s === 'wishlist') return '★ WANTED';
  return '✓ COLLECTED';
}
function statusColor(s) {
  if (s === 'for_sale') return 'var(--purple)';
  if (s === 'trade') return 'var(--cyan)';
  if (s === 'wishlist') return 'var(--gold)';
  return 'var(--green)';
}

function ModalPhoto({ userId, cardKey, side, label }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const u = localImageUrl(userId, cardKey, side);
    const img = new Image();
    img.onload = () => setUrl(u);
    img.onerror = () => setUrl(null);
    img.src = u;
  }, [userId, cardKey, side]);
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
      <div style={{ width: '100%', aspectRatio: '0.72', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {url
          ? <img src={url} alt={label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          : <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', color: 'var(--border-2)', letterSpacing: '1px' }}>NO PHOTO</span>}
      </div>
      <span style={{ fontFamily: "'Orbitron',monospace", fontSize: '7px', letterSpacing: '2px', color: 'var(--muted-2)' }}>{label}</span>
    </div>
  );
}


// Picks the best available community photo for a card:
// Priority: base > refractor > parallels by descending print run
function BestPhoto({ communityMap, setId, subSetId, cardN, selectedParallel, ALL_PARALLELS }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    // A card's thumbnail should use the first collector that actually HAS a
    // stored photo, not just the first collector row. communityMap is built
    // from collections rows and has no per-entry "has photo" flag (a collector
    // can mark a card collected without uploading an image), so we detect it by
    // attempting each candidate's image URL and advancing on error.
    let cancelled = false;

    // Probe {userId, key} candidates in order; setUrl to the first that loads.
    // Runs onDone() when the list is exhausted (used to fall base -> parallels).
    function probe(candidates, onDone) {
      let i = 0;
      (function next() {
        if (cancelled) return;
        if (i >= candidates.length) { onDone(); return; }
        const { userId, key } = candidates[i++];
        const u = localImageUrl(userId, key, 'front');
        const img = new Image();
        img.onload = () => { if (!cancelled) setUrl(u); };
        img.onerror = next;
        img.src = u;
      })();
    }

    // If a specific parallel is selected, use entries for that parallel.
    if (selectedParallel && selectedParallel !== 'all') {
      const lk = setId + '__' + subSetId + '__' + cardN + '__parallel__' + selectedParallel;
      const d = communityMap[lk];
      const entries = d ? [...(d.serials || []), ...(d.entries || [])] : [];
      if (entries.length > 0) {
        const list = entries
          .map(entry => ({ userId: entry.userId, key: lk.replace('__s__' + (entry.serialNum || ''), '') }));
        probe(list, () => { if (!cancelled) setUrl(null); });
      }
      return () => { cancelled = true; };
    }

    // ALL CARDS view — try base first, then refractor, then parallels by print run desc
    const baseKey = setId + '__' + subSetId + '__' + cardN;
    const baseData = communityMap[baseKey];
    const baseEntries = baseData?.entries || [];
    const baseList = baseEntries.map(e => ({ userId: e.userId, key: baseKey }));
    probe(baseList, tryParallels);

    function tryParallels() {
      if (cancelled) return;
      // Build list of parallel keys for this card, sorted by print run descending (rarest last = base first)
      const cardPrefix = baseKey + '__parallel__';
      const parKeys = Object.keys(communityMap)
        .filter(k => k.startsWith(cardPrefix) && !k.includes('__s__'));

      // Sort: refractor first, then by print_run descending (largest print run = most common = try last)
      const sorted = parKeys.sort((a, b) => {
        const aId = a.replace(cardPrefix, '');
        const bId = b.replace(cardPrefix, '');
        const aPar = ALL_PARALLELS.find(p => p.id === aId);
        const bPar = ALL_PARALLELS.find(p => p.id === bId);
        if (aId === 'refractor') return -1;
        if (bId === 'refractor') return 1;
        const aPr = aPar?.print_run || 9999;
        const bPr = bPar?.print_run || 9999;
        return bPr - aPr; // descending — we'll try in order so base-like first
      });

      // Flatten parallel keys into candidates (every entry per key).
      const list = [];
      sorted.forEach(k => {
        const d = communityMap[k];
        const entries = [...(d?.serials || []), ...(d?.entries || [])];
        entries.forEach(e => list.push({ userId: e.userId, key: k }));
      });
      probe(list, () => { if (!cancelled) setUrl(null); });
    }

    return () => { cancelled = true; };
  }, [communityMap, setId, subSetId, cardN, selectedParallel]);

  if (!url) return <div style={BP.empty} />;
  return (
    <div style={BP.wrap}>
      <img src={url} alt="card" style={BP.img} />
    </div>
  );
}

const BP = {
  wrap: { width: '44px', height: '62px', flexShrink: 0, borderRadius: '2px', overflow: 'hidden', background: 'var(--bg-2)', border: '1px solid var(--border)' },
  img: { width: '100%', height: '100%', objectFit: 'contain' },
  empty: { width: '44px', height: '62px', flexShrink: 0, borderRadius: '2px', background: 'var(--panel-deep3)', border: '1px solid var(--panel-3)' },
};

export default function CommunitySetView({ setId }) {
  // All published sets, loaded from Supabase (same source as My Checklists +
  // Community Checklists). setMeta is derived from this list so navigating
  // between sets stays in sync without a hardcoded registry.
  const [allSets, setAllSets] = useState([]);
  useEffect(() => {
    let cancelled = false;
    loadPublishedSets().then(sets => { if (!cancelled) setAllSets(sets || []); });
    return () => { cancelled = true; };
  }, []);
  const setMeta = useMemo(
    () => allSets.find(s => s.id === setId) || null,
    [allSets, setId]
  );
  const [SETS, setSETS] = useState([]);
  // Per-set parallel exports from the set's own JS module. Resolved exactly like
  // Checklist.js so the community view shows the SAME parallels collectors see on
  // the main checklist — including correctly showing NONE for parallel-free sets
  // (e.g. vintage 1977), instead of falling back to a generic modern ladder.
  const [SET_PARALLELS, setSET_PARALLELS] = useState([]);
  const [SET_AUTO_PARALLELS, setSET_AUTO_PARALLELS] = useState([]);
  const [SET_INSERT_PARALLELS, setSET_INSERT_PARALLELS] = useState([]);
  const [communityMap, setCommunityMap] = useState({});
  const [totalCollectors, setTotalCollectors] = useState(0);
  const [selectedParallel, setSelectedParallel] = useState('all');
  const [filterCard, setFilterCard] = useState('all');
  const [filterSerial, setFilterSerial] = useState('all');
  const [openSections, setOpenSections] = useState({});
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(true);

  const nav = path => { window.history.pushState({}, '', path); window.dispatchEvent(new PopStateEvent('popstate')); };

  useEffect(() => {
    if (!setMeta) return;
    import(`./sets/${setMeta.dataFile}.js`).then(m => {
      const sets = (m.SETS || []).filter(s => s.type !== 'sketch');
      setSETS(sets);
      // Mirror Checklist.js: the set's own exports drive the parallel ladder for
      // any sub-set without an inline `parallels` array.
      setSET_PARALLELS(m.PARALLELS || []);
      setSET_AUTO_PARALLELS(m.AUTO_PARALLELS || m.PARALLELS || []);
      setSET_INSERT_PARALLELS(m.INSERT_PARALLELS || m.PARALLELS || []);
      const open = {};
      sets.forEach(s => { open[s.id] = false; });
      setOpenSections(open);
    });
  // Depend on setMeta (not just setId) because setMeta loads asynchronously
  // from Supabase — the first render has setMeta=null and we need to re-run
  // once it resolves.
  }, [setMeta]);

  useEffect(() => {
    if (!setMeta) return;
    (async () => {
      const { data: profiles } = await supabase.from('public_profiles').select('id,username').eq('is_public', true);
      if (!profiles?.length) { setLoading(false); return; }
      const pMap = {};
      profiles.forEach(p => { pMap[p.id] = p; });
      setTotalCollectors(profiles.length);
      // Paged: big sets (2026 Galaxy) exceed the 1000-row cap.
      const rows = await fetchAllRows(() => supabase.from('collections')
        .select('card_key,user_id,status,sale_price,grading_company,grading_grade,grading_cert_num,grading_serial_num,grading_print_run,grading_tag_score,grading_report_url,grading_pop_this,grading_pop_higher')
        .in('user_id', profiles.map(p => p.id))
        .like('card_key', setId + '__%')
        .order('user_id').order('card_key'))
        .catch(err => { console.error('CommunitySetView: failed to load collections', err); return null; });
      if (rows) {
        const map = {};
        rows.forEach(row => {
          let key = row.card_key;
          let sn = null;
          if (key.includes('__s__')) { const i = key.lastIndexOf('__s__'); sn = key.slice(i + 5); key = key.slice(0, i); }
          if (!map[key]) map[key] = { entries: [], serials: [] };
          const prof = pMap[row.user_id] || {};
          const e = { userId: row.user_id, username: prof.username || '?', status: row.status, salePrice: row.sale_price, gradingCompany: row.grading_company, gradingGrade: row.grading_grade, gradingCertNum: row.grading_cert_num, gradingTagScore: row.grading_tag_score, gradingReportUrl: row.grading_report_url, gradingPopThis: row.grading_pop_this, gradingPopHigher: row.grading_pop_higher, cardKey: row.card_key };
          if (sn !== null) { map[key].serials.push({ ...e, serialNum: sn }); }
          else { map[key].entries.push(e); }
        });
        Object.values(map).forEach(d => d.serials.sort((a, b) => parseInt(a.serialNum) - parseInt(b.serialNum)));
        setCommunityMap(map);
      }
      setLoading(false);
    })();
  // Depend on setMeta — community-collection data depends on setId, and
  // setMeta resolution gates the early `if (!setMeta) return` above. Re-run
  // when setMeta loads or changes.
  }, [setMeta]);

  const availableParallels = useMemo(() => {
    const ids = new Set();
    Object.keys(communityMap).forEach(k => { if (k.includes('__parallel__')) ids.add(k.split('__parallel__')[1]); });
    return [...ids].map(id => PAR_MAP[id] || { id, name: id, print_run: null, color: 'var(--text)' })
      .sort((a, b) => (a.print_run || 9999) - (b.print_run || 9999));
  }, [communityMap]);

  const totalSerials = useMemo(() => Object.values(communityMap).reduce((n, d) => n + d.serials.length, 0), [communityMap]);

  // Cards that have community data for the current parallel
  const cardsWithData = useMemo(() => {
    const result = new Set();
    if (!SETS.length) return result;
    SETS.forEach(sub => {
      sub.cards.forEach(card => {
        const bk = setId + '__' + sub.id + '__' + card.n;
        const lk = selectedParallel === 'all' ? bk : bk + '__parallel__' + selectedParallel;
        if (communityMap[lk]) result.add(String(card.n));
      });
    });
    return result;
  }, [SETS, communityMap, setId, selectedParallel]);

  // Serials available for current set + parallel + card filter
  const availableSerials = useMemo(() => {
    if (selectedParallel === 'all') return [];
    const serials = new Set();
    SETS.forEach(sub => {
      sub.cards.forEach(card => {
        if (filterCard !== 'all' && String(card.n) !== filterCard) return;
        const lk = setId + '__' + sub.id + '__' + card.n + '__parallel__' + selectedParallel;
        const d = communityMap[lk];
        if (d?.serials) d.serials.forEach(s => serials.add(s.serialNum));
      });
    });
    return [...serials].sort((a, b) => parseInt(a) - parseInt(b));
  }, [SETS, communityMap, setId, selectedParallel, filterCard]);

  const selPar = selectedParallel !== 'all' ? (PAR_MAP[selectedParallel] || null) : null;
  const isNumbered = selPar?.print_run != null;

  const toggleExpand = key => setExpandedKeys(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });

  if (!setMeta) return <div style={V.screen}><span style={V.screenTxt}>SET NOT FOUND</span></div>;
  if (loading) return <div style={V.screen}><span style={V.screenTxt}>LOADING COMMUNITY DATA...</span></div>;

  return (
    <div style={V.page}>
      <NavDrawer />
      <div style={V.stars} />

      {/* Header */}
      <header style={V.header}>
        <div style={V.hInner}>
          <div>
            <div style={V.logoLine}>COMMUNITY CHECKLIST · {setMeta.brand.toUpperCase()}</div>
            <h1 style={V.bigTitle}>COMMUNITY CHECKLIST</h1>
            <div style={V.subTitle}>{setMeta.shortName}</div>
          </div>

        </div>
        <div style={{ height: '1px', background: `linear-gradient(90deg,transparent,${lightAccent(setMeta.color)},${lightAccent(setMeta.accentColor||setMeta.color)},${lightAccent(setMeta.color)},transparent)` }} />
      </header>

      {/* Stats */}
      <div style={V.statsBar}>
        <span style={{ ...V.pill, borderColor: 'var(--cyan)', color: 'var(--cyan)' }}>{totalCollectors} COLLECTOR{totalCollectors !== 1 ? 'S' : ''}</span>
        <span style={{ ...V.pill, borderColor: 'var(--brand-gold)', color: 'var(--brand-gold)' }}>{totalSerials} SERIAL{totalSerials !== 1 ? 'S' : ''} TRACKED</span>
      </div>

      {/* Filters */}
      <div style={V.filterBar}>
        <span style={V.filterLabel}>FILTER</span>

        {/* Set selector — navigate to another community checklist */}
        <select value={setId} onChange={e => { if (e.target.value !== setId) nav('/community/' + e.target.value); }}
          style={V.select}>
          {allSets.map(s => <option key={s.id} value={s.id}>{s.shortName}</option>)}
        </select>

        {/* Parallel */}
        <select value={selectedParallel} onChange={e => { setSelectedParallel(e.target.value); setExpandedKeys(new Set()); setFilterCard('all'); setFilterSerial('all'); }}
          style={{ ...V.select, borderColor: selPar ? lightAccent(selPar.color) : 'var(--border)', minWidth: '160px' }}>
          <option value="all">ALL CARDS (BASE)</option>
          {availableParallels.map(p => (
            <option key={p.id} value={p.id}>{p.name}{p.print_run ? ` /${p.print_run}` : ''}</option>
          ))}
        </select>

        {/* Card # — only show cards with community data */}
        {cardsWithData.size > 0 && (
          <select value={filterCard} onChange={e => { setFilterCard(e.target.value); setFilterSerial('all'); }}
            style={V.select}>
            <option value="all">ALL CARDS</option>
            {[...cardsWithData].sort((a,b) => parseInt(a)-parseInt(b)).map(n => (
              <option key={n} value={n}>Card #{n}</option>
            ))}
          </select>
        )}

        {/* Serial # — only when a numbered parallel is selected and serials exist */}
        {availableSerials.length > 0 && (
          <select value={filterSerial} onChange={e => setFilterSerial(e.target.value)} style={V.select}>
            <option value="all">ALL SERIALS</option>
            {availableSerials.map(n => <option key={n} value={n}>#{n}</option>)}
          </select>
        )}

        {selPar && (
          <span style={{ fontFamily: "'Orbitron',monospace", fontSize: '8px', padding: '4px 8px', border: '1px solid', borderColor: lightAccent(selPar.color), color: lightAccent(selPar.color), borderRadius: '1px', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
            {selPar.name}{selPar.print_run ? ` /${selPar.print_run}` : ''}
          </span>
        )}
      </div>

      {/* Sections */}
      <main style={V.main}>
        {SETS.map(sub => {
          const isOpen = openSections[sub.id] !== false;
          const tColor = sub.type === 'base' ? 'var(--cyan)' : sub.type === 'insert' ? 'var(--purple)' : 'var(--gold)';
          const withData = sub.cards.reduce((n, card) => {
            const bk = setId + '__' + sub.id + '__' + card.n;
            const lk = selectedParallel === 'all' ? bk : bk + '__parallel__' + selectedParallel;
            const d = communityMap[lk];
            const cnt = selectedParallel === 'all' ? (d?.entries.length||0) : isNumbered ? (d?.serials.length||0) : (d?.entries.length||0);
            return n + (cnt > 0 ? 1 : 0);
          }, 0);

          return (
            <section key={sub.id} style={V.section}>
              <button style={{ ...V.secBtn, borderLeftColor: tColor }} onClick={() => setOpenSections(p => ({ ...p, [sub.id]: !isOpen }))}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ ...V.secTitle, color: tColor }}>{sub.title}</span>
                  <span style={V.secCount}>{sub.cards.length} CARDS</span>
                  {withData > 0 && <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', color: 'var(--brand-gold)' }}>{withData} WITH DATA</span>}
                </div>
                <span style={{ color: 'var(--muted-2)', fontSize: '14px', display: 'inline-block', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
              </button>

              {isOpen && (
                <div>
                  {sub.cards.map(card => {
                    const bk = setId + '__' + sub.id + '__' + card.n;
                    const lk = selectedParallel === 'all' ? bk : bk + '__parallel__' + selectedParallel;
                    // Apply card number filter
                    if (filterCard !== 'all' && String(card.n) !== filterCard) return null;
                    const d = communityMap[lk];
                    let entries = selectedParallel === 'all' ? (d?.entries||[]) : isNumbered ? (d?.serials||[]) : (d?.entries||[]);
                    // Apply serial filter
                    if (filterSerial !== 'all' && isNumbered) {
                      entries = entries.filter(e => e.serialNum === filterSerial);
                    }
                    const count = entries.length;
                    const expanded = expandedKeys.has(lk);
                    const bColor = lightAccent(selPar?.color || '#00d4ff');

                    // Parallel list for this sub-set (only when showing all parallels).
                    // Resolved identically to Checklist.js (line 919-920): an inline
                    // `parallels` array — even an empty one — is authoritative, so
                    // parallel-free sets (e.g. 1977) correctly show none. Only a
                    // missing inline array falls back to the set's own exports, then
                    // the type default.
                    const _sp = sub.type === 'auto' ? SET_AUTO_PARALLELS
                      : sub.type === 'insert' ? SET_INSERT_PARALLELS
                      : SET_PARALLELS;
                    const subParallels = selectedParallel === 'all'
                      ? (sub.parallels || (_sp.length > 0 ? _sp : getParallelsForType(sub.type)))
                      : [];
                    const parAccordionKey = bk + '__parallels_accordion';
                    const parAccordionOpen = expandedKeys.has(parAccordionKey);
                    const hasAnyParData = subParallels.some(p => {
                      const pk = bk + '__parallel__' + p.id;
                      const pd = communityMap[pk];
                      const isNum = p.print_run && p.print_run > 1;
                      return isNum ? (pd?.serials?.length || 0) > 0 : (pd?.entries?.length || 0) > 0;
                    });

                    return (
                      <div key={card.n} style={{ borderBottom: '1px solid rgba(var(--border-rgb),0.4)' }}>
                        {/* Base card row */}
                        <div style={{ ...V.cardRow, cursor: count > 0 ? 'pointer' : 'default', background: expanded ? 'rgba(var(--cyan-rgb),0.03)' : 'transparent' }}
                          onClick={() => count > 0 && toggleExpand(lk)}>
                          <span style={V.cardNum}>#{card.n}</span>
                          <BestPhoto communityMap={communityMap} setId={setId} subSetId={sub.id} cardN={card.n} selectedParallel={selectedParallel} ALL_PARALLELS={ALL_PARALLELS} />
                          <div style={V.cardInfo}>
                            <span style={V.cardName}>{card.name}</span>
                            {card.artist && <span style={V.cardArtist}>{card.artist}</span>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                            {count > 0 && (
                              <span style={{ fontFamily: "'Orbitron',monospace", fontSize: '8px', letterSpacing: '0.5px', padding: '3px 8px', border: '1px solid', borderRadius: '1px', borderColor: bColor, color: bColor, background: bColor + '18' }}>
                                {isNumbered ? `${count} SERIAL${count !== 1 ? 'S' : ''}` : `${count} COLLECTOR${count !== 1 ? 'S' : ''}`}
                              </span>
                            )}
                            {count > 0 && (
                              <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', color: 'var(--muted-2)' }}>
                                {expanded ? '▲' : '▼'}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Expanded base card collector list */}
                        {expanded && count > 0 && (
                          <div style={V.expandPanel}>
                            {entries.map((entry, idx) => {
                              const hasGrade = entry.gradingCompany && entry.gradingGrade;
                              const gc = entry.gradingCompany === 'TAG' ? 'var(--brand-gold)' : entry.gradingCompany === 'PSA' ? 'var(--blue)' : entry.gradingCompany === 'BGS' ? 'var(--err)' : 'var(--text)';
                              return (
                                <div key={idx} style={V.entryRow}
                                  onClick={() => setModal({ ...entry, cardNum: card.n, cardName: card.name, subSetTitle: sub.title, selPar })}>
                                  {isNumbered && <span style={V.entrySerial}>#{entry.serialNum}/{selPar.print_run}</span>}
                                  {hasGrade && <span style={{ ...V.entryGrade, color: gc }}>{entry.gradingCompany} {entry.gradingGrade}</span>}
                                  <span style={V.entryUser}>@{entry.username}</span>
                                  <span style={{ fontFamily: "'Orbitron',monospace", fontSize: '8px', padding: '2px 7px', border: '1px solid', borderRadius: '1px', borderColor: statusColor(entry.status) + '66', color: statusColor(entry.status), background: statusColor(entry.status) + '18', whiteSpace: 'nowrap' }}>
                                    {statusLabel(entry.status, entry.salePrice)}
                                  </span>
                                  <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', color: 'var(--cyan)', marginLeft: 'auto', flexShrink: 0 }}>VIEW →</span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Single PARALLELS accordion per card */}
                        {subParallels.length > 0 && (
                          <div>
                            <div style={{ ...V.cardRow, paddingLeft: '32px', background: 'var(--bg)', cursor: 'pointer', borderTop: '1px solid var(--panel-deep)' }}
                              onClick={() => toggleExpand(parAccordionKey)}>
                              <span style={{ fontFamily: "'Orbitron',monospace", fontSize: '8px', letterSpacing: '2px', color: 'var(--brand-gold)', flex: 1 }}>PARALLELS</span>
                              {hasAnyParData && <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', color: 'var(--cyan)', marginRight: '8px' }}>● HAS DATA</span>}
                              <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', color: 'var(--muted-2)' }}>{parAccordionOpen ? '▲' : '▼'}</span>
                            </div>
                            {parAccordionOpen && (
                              <div>
                                {subParallels.map(p => {
                                  const pk = bk + '__parallel__' + p.id;
                                  const pd = communityMap[pk];
                                  const isNumPar = p.print_run && p.print_run > 1;
                                  const is1of1 = p.print_run === 1;
                                  const parEntries = isNumPar ? (pd?.serials || []) : (pd?.entries || []);
                                  const parCount = parEntries.length;
                                  const parExpanded = expandedKeys.has(pk);
                                  return (
                                    <div key={p.id}>
                                      <div style={{ ...V.cardRow, paddingLeft: '48px', background: parExpanded ? lightAccent(p.color) + '08' : 'var(--bg-2)', cursor: parCount > 0 ? 'pointer' : 'default', borderTop: '1px solid var(--bg-3)' }}
                                        onClick={() => parCount > 0 && toggleExpand(pk)}>
                                        <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: parCount > 0 ? lightAccent(p.color) : 'var(--border)', flexShrink: 0 }} />
                                        <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', color: parCount > 0 ? lightAccent(p.color) : 'var(--border-2)', flex: 1, marginLeft: '8px' }}>
                                          {p.name}{is1of1 ? ' 1/1' : p.print_run ? ` /${p.print_run}` : ''}
                                        </span>
                                        {parCount > 0 && (
                                          <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', color: 'var(--muted-2)', marginRight: '8px' }}>
                                            {isNumPar ? `${parCount} serial${parCount !== 1 ? 's' : ''}` : `${parCount} collector${parCount !== 1 ? 's' : ''}`}
                                          </span>
                                        )}
                                        {parCount > 0 && <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', color: 'var(--border-2)' }}>{parExpanded ? '▲' : '▼'}</span>}
                                      </div>
                                      {parExpanded && parCount > 0 && (
                                        <div style={V.expandPanel}>
                                          {parEntries.map((entry, idx) => {
                                            const hasGrade = entry.gradingCompany && entry.gradingGrade;
                                            const gc = entry.gradingCompany === 'TAG' ? 'var(--brand-gold)' : entry.gradingCompany === 'PSA' ? 'var(--blue)' : entry.gradingCompany === 'BGS' ? 'var(--err)' : 'var(--text)';
                                            return (
                                              <div key={idx} style={V.entryRow}
                                                onClick={() => setModal({ ...entry, cardNum: card.n, cardName: card.name, subSetTitle: sub.title, selPar: p })}>
                                                {isNumPar && <span style={V.entrySerial}>#{entry.serialNum}/{p.print_run}</span>}
                                                {hasGrade && <span style={{ ...V.entryGrade, color: gc }}>{entry.gradingCompany} {entry.gradingGrade}</span>}
                                                <span style={V.entryUser}>@{entry.username}</span>
                                                <span style={{ fontFamily: "'Orbitron',monospace", fontSize: '8px', padding: '2px 7px', border: '1px solid', borderRadius: '1px', borderColor: statusColor(entry.status) + '66', color: statusColor(entry.status), background: statusColor(entry.status) + '18', whiteSpace: 'nowrap' }}>
                                                  {statusLabel(entry.status, entry.salePrice)}
                                                </span>
                                                <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', color: 'var(--cyan)', marginLeft: 'auto', flexShrink: 0 }}>VIEW →</span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </main>

      {/* Detail Modal */}
      {modal && (
        <div style={V.overlay} onClick={() => setModal(null)}>
          <div style={V.modal} onClick={e => e.stopPropagation()}>
            <button style={V.closeBtn} onClick={() => setModal(null)}>✕</button>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontFamily: "'Orbitron',monospace", fontSize: '12px', fontWeight: 700, color: 'var(--text-strong)', marginBottom: '4px' }}>
                #{modal.cardNum} · {modal.cardName}
              </div>
              <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', color: 'var(--muted-2)' }}>
                {modal.subSetTitle}
                {modal.selPar ? ` · ${modal.selPar.name}${modal.selPar.print_run ? ` /${modal.selPar.print_run}` : ''}` : ''}
                {modal.serialNum != null ? ` · Serial #${modal.serialNum}` : ''}
              </div>
            </div>

            {/* Photos */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
              <ModalPhoto userId={modal.userId} cardKey={modal.cardKey} side="front" label="FRONT" />
              <ModalPhoto userId={modal.userId} cardKey={modal.cardKey} side="back" label="BACK" />
            </div>

            {/* Info */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <div style={V.infoBlock}>
                <div style={V.infoBlockTitle}>COLLECTOR</div>
                <div style={V.infoRow}><span style={V.iKey}>Username</span><span style={V.iVal}>@{modal.username}</span></div>
                <div style={V.infoRow}>
                  <span style={V.iKey}>Status</span>
                  <span style={{ fontFamily: "'Orbitron',monospace", fontSize: '8px', padding: '3px 8px', border: '1px solid', borderRadius: '1px', borderColor: statusColor(modal.status) + '66', color: statusColor(modal.status), background: statusColor(modal.status) + '18' }}>
                    {statusLabel(modal.status, modal.salePrice)}
                  </span>
                </div>
              </div>

              {(modal.gradingCompany || modal.gradingGrade) && (
                <div style={V.infoBlock}>
                  <div style={V.infoBlockTitle}>GRADING</div>
                  {modal.gradingCompany && <div style={V.infoRow}><span style={V.iKey}>Company</span><span style={V.iVal}>{modal.gradingCompany}</span></div>}
                  {modal.gradingGrade && <div style={V.infoRow}><span style={V.iKey}>Grade</span><span style={{ ...V.iVal, color: 'var(--brand-gold)', fontWeight: 700, fontSize: '20px' }}>{modal.gradingGrade}</span></div>}
                  {modal.gradingTagScore && <div style={V.infoRow}><span style={V.iKey}>TAG Score</span><span style={V.iVal}>{modal.gradingTagScore}</span></div>}
                  {modal.gradingCertNum && <div style={V.infoRow}><span style={V.iKey}>Cert #</span><span style={V.iVal}>{modal.gradingCertNum}</span></div>}
                  {(modal.gradingPopThis || modal.gradingPopHigher) && <div style={V.infoRow}><span style={V.iKey}>Pop</span><span style={V.iVal}>{modal.gradingPopThis||0} this · {modal.gradingPopHigher||0} higher</span></div>}
                  {modal.gradingReportUrl && <div style={V.infoRow}><span style={V.iKey}>Report</span><a href={modal.gradingReportUrl} target="_blank" rel="noopener noreferrer" style={{ ...V.iVal, color: 'var(--cyan)', textDecoration: 'none' }}>VIEW ↗</a></div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const V = {
  page: { minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'Rajdhani',sans-serif" },
  stars: { position: 'fixed', inset: 0, opacity: 'var(--stars-opacity)', pointerEvents: 'none', zIndex: 0, backgroundImage: 'radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.35) 0%, transparent 100%), radial-gradient(1px 1px at 80% 60%, rgba(255,255,255,0.25) 0%, transparent 100%), radial-gradient(1px 1px at 50% 80%, rgba(255,255,255,0.2) 0%, transparent 100%)' },
  screen: { minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  screenTxt: { fontFamily: "'Orbitron',monospace", fontSize: '11px', letterSpacing: '4px', color: 'var(--muted-2)' },
  header: { position: 'relative', zIndex: 10, background: 'rgba(var(--bg-rgb),0.97)', padding: '20px 24px 0', borderBottom: '1px solid var(--border)' },
  hInner: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', maxWidth: '1200px', margin: '0 auto', paddingBottom: '16px' },
  logoLine: { fontFamily: "'Orbitron',monospace", fontSize: '8px', letterSpacing: '4px', color: 'var(--brand-gold)', opacity: 0.85, marginBottom: '6px' },
  bigTitle: { fontFamily: "'Orbitron',monospace", fontSize: 'clamp(18px,3vw,28px)', fontWeight: 900, color: 'var(--text-strong)', margin: '0 0 4px' },
  subTitle: { fontFamily: "'Orbitron',monospace", fontSize: 'clamp(11px,2vw,14px)', fontWeight: 600, color: 'var(--brand-gold)', letterSpacing: '2px', opacity: 0.9 },
  title: { fontFamily: "'Orbitron',monospace", fontSize: 'clamp(16px,3vw,26px)', fontWeight: 900, color: 'var(--text-strong)', margin: 0 },
  hActions: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  btn: { fontFamily: "'Orbitron',monospace", fontSize: '8px', letterSpacing: '1px', color: 'var(--muted-2)', background: 'transparent', border: '1px solid var(--border)', padding: '7px 12px', borderRadius: '2px', cursor: 'pointer' },
  statsBar: { display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', padding: '10px 20px', background: 'rgba(var(--bg-rgb),0.96)', borderBottom: '1px solid var(--border)', position: 'relative', zIndex: 10 },
  pill: { fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', padding: '4px 10px', borderRadius: '2px', border: '1px solid var(--border)' },
  filterBar: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 24px', background: 'var(--bg-sticky)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 20 },
  filterLabel: { fontFamily: "'Orbitron',monospace", fontSize: '8px', letterSpacing: '2px', color: 'var(--muted-2)', flexShrink: 0 },
  select: { background: 'var(--panel)', border: '1px solid', color: 'var(--text)', fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', padding: '6px 10px', borderRadius: '2px', cursor: 'pointer', outline: 'none', transition: 'border-color 0.2s', minWidth: '220px' },
  main: { position: 'relative', zIndex: 1, padding: '16px 20px 60px', maxWidth: '1200px', margin: '0 auto' },
  section: { marginBottom: '10px', border: '1px solid var(--border)', borderRadius: '2px', overflow: 'hidden' },
  secBtn: { width: '100%', background: 'rgba(var(--pcard-rgb),0.9)', border: 'none', borderLeft: '3px solid', padding: '11px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' },
  secTitle: { fontFamily: "'Orbitron',monospace", fontSize: '10px', letterSpacing: '2px', fontWeight: 700 },
  secCount: { fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', color: 'var(--muted-2)' },
  cardRow: { display: 'flex', alignItems: 'center', padding: '9px 16px', gap: '12px', transition: 'background 0.15s' },
  cardNum: { fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--muted-2)', minWidth: '38px', flexShrink: 0 },
  cardInfo: { flex: 1, minWidth: 0 },
  cardName: { fontFamily: "'Rajdhani',sans-serif", fontWeight: 600, fontSize: '14px', color: 'var(--text)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardArtist: { fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', color: 'var(--muted-2)', display: 'block' },
  expandPanel: { background: 'var(--bg-sticky)', borderTop: '1px solid var(--border)' },
  entryRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 16px 9px 30px', cursor: 'pointer', borderBottom: '1px solid rgba(var(--border-rgb),0.3)', transition: 'background 0.15s' },
  entrySerial: { fontFamily: "'Orbitron',monospace", fontSize: '10px', color: 'var(--brand-gold)', fontWeight: 700, minWidth: '64px', flexShrink: 0 },
  entryGrade: { fontFamily: "'Orbitron',monospace", fontSize: '9px', fontWeight: 700, minWidth: '52px', flexShrink: 0 },
  entryUser: { fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--cyan)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(var(--bg-rgb),0.93)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(6px)' },
  modal: { background: 'var(--panel)', border: '1px solid var(--border-2)', borderRadius: '4px', padding: '24px', maxWidth: '540px', width: '100%', position: 'relative', maxHeight: '90vh', overflowY: 'auto' },
  closeBtn: { position: 'absolute', top: '12px', right: '12px', background: 'transparent', border: 'none', color: 'var(--muted-2)', fontSize: '18px', cursor: 'pointer', lineHeight: 1, padding: '2px 6px' },
  infoBlock: { flex: '1 1 180px', background: 'var(--panel-deep3)', border: '1px solid var(--border)', borderRadius: '2px', padding: '12px' },
  infoBlockTitle: { fontFamily: "'Orbitron',monospace", fontSize: '8px', letterSpacing: '2px', color: 'var(--muted-2)', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid var(--border)' },
  infoRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '7px', flexWrap: 'wrap' },
  iKey: { fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', color: 'var(--muted-2)', flexShrink: 0 },
  iVal: { fontFamily: "'Rajdhani',sans-serif", fontSize: '13px', fontWeight: 600, color: 'var(--text)', textAlign: 'right' },
};
